import * as React from 'react'
import mustache from 'mustache'
import Navigation from '../../components/Messenger/Navigation'
import styled, { css, useTheme } from 'styled-components'
import CloseButton from '../../components/Messenger/Navigation/CloseButton'
import { AppState } from '../../store'
import { useSelector } from 'react-redux'
import WidgetCard from '../../components/Home/WidgetCard'
import MobilityHelper from '../../../helpers/MobilityHelper'
import { useTranslation } from 'react-i18next'
import Avatar from '../../../help_center/components/Avatar/Avatar'
import StackedAvatars from '../../../help_center/components/Avatar/StackedAvatars'
import { WidgetHelper } from '../../helpers'
import { rgba } from 'polished'
import FadeInContainer from '../../components/Messenger/Animation/FadeInContainer'
import { MOBILE_BREAKPOINT } from '../../styles/breakpoints'
import ScrollToTopOnMount from '../../effects/ScrollToTopOnMount'

const Header = styled.div<{ mobile: boolean }>`
  padding: 32px 36px 70px;
  box-sizing: border-box;
  background-repeat: no-repeat;
  position: relative;
  color: ${({ theme }) => theme.color.backgroundText};
  opacity: 1;

  ${StackedAvatars} {
    ${props => props.mobile && css`
      margin-right: 24px;
    `}
  }
`

const CoverFade = styled.div`
  display: none;
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: 100%;
  opacity: 1;
  z-index: 0;
  background: linear-gradient(rgba(255, 255, 255, 0) 0%, rgb(255, 255, 255) 100%);
  height: 80px;
`

const Cover = styled.div<{ backgroundImageUrl?: string, height: number, fade: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: -1;
  height: ${props => props.height}px;
  background: ${({ theme }) => theme.color.background};
  background-size: 100%;
  background-position: left top;

  ${props => props.backgroundImageUrl && css`
    background-image: url(${props.backgroundImageUrl});
  `}

  ${props => props.fade && css`
    ${CoverFade} {
      display: flex;
    }
  `}
`

const HeaderTop = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 70px;
`

const HeaderLogo = styled.img`
  max-height: 32px;
  max-width: 100%;
`

const Title = styled.div`
  overflow-wrap: break-word;
  font-weight: 700;
  font-size: 32px;
  line-height: 38px;
  color: ${({ theme }) => theme.color.backgroundText};
  opacity: .6;
`

const Subtitle = styled(Title)`
  color: ${({ theme }) => theme.color.backgroundText};
  text-shadow: rgba(255, 255, 255, 0.3) 0px 0px 30px;
  opacity: 1;
`

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: -47px;
  padding: 0 16px;
  z-index: 1;
  margin-bottom: 16px;
`

const CloseButtonContainer = styled.div`
  position: fixed;
  top: 28px;
  right: 16px;
  z-index: 9999;
  border-radius: 10px;
  color: ${props => props.theme.backgroundText};

  > div {
    background: ${({ theme }) => rgba(theme.color.background, 0.5)};

    &:hover {
      background-color: ${({ theme }) => theme.color.background};
    }

    svg {
      color: white;
    }
  }
`

const Home = () => {
  const { t } = useTranslation()
  const { bizzeySetting: { name }, widget, locale } = useSelector((state: AppState) => state.application)
  const mobile: boolean = useSelector<AppState>(state => Boolean(state?.application?.hostMobile)) as boolean

  let title = MobilityHelper.getTranslation(locale, widget.translations, 'welcome_message')

  try {
    title = mustache.render(title, { name: name || '' })
  } catch (error) {
    console.log(error.message)
  }

  return (
    <FadeInContainer>
      <Navigation hidden />
      <ScrollToTopOnMount />

      <CloseButtonContainer>
        <CloseButton />
      </CloseButtonContainer>

      <Cover
        height={Number(widget.header_height)}
        fade={widget.header_background_fade}
        backgroundImageUrl={widget.header_image_url}
      >
        <CoverFade />
      </Cover>
      <Header mobile={mobile}>
        <HeaderTop>
          <HeaderLogo
            src={WidgetHelper.logoUrl(widget.background_color)}
          />

          {widget?.show_avatars && widget?.agents?.length > 0 && (<>
            <StackedAvatars noBorder disableHide>
              {widget?.agents?.slice(0, 3).map((agent) => {
                return (
                  <Avatar
                    key={agent.id}
                    name={agent.name}
                    src={agent.avatar_url}
                    width={40}
                  />
                )
              })}
            </StackedAvatars>
          </>)}
        </HeaderTop>

        <Title>{title}</Title>
        <Subtitle>{t('Home::How can we help?')}</Subtitle>
      </Header>

      <Cards>
        {widget?.cards?.map((card) => {
          return (
            <WidgetCard
              key={card.id}
              widget={widget}
              locale={locale}
              card={card}
            />
          )
        })}
      </Cards>
    </FadeInContainer>
  )
}

export default Home